import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { BBPS_CATEGORIES, CATEGORY_LABEL, RAIL_LABEL, formatINR } from "@/lib/constants";
import { CopyableId } from "@/components/CopyableId";
import { toast } from "sonner";
import { Download, Filter, RotateCcw, Eye, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  success: "bg-success text-success-foreground",
  failed: "bg-destructive text-destructive-foreground",
  pending: "bg-warning text-warning-foreground",
  pending_l1: "bg-warning/15 text-warning border-warning/30",
  pending_l2: "bg-warning/15 text-warning border-warning/30",
  pending_l3: "bg-warning/15 text-warning border-warning/30",
  processing: "bg-primary/15 text-primary border-primary/30",
  rejected: "bg-destructive text-destructive-foreground",
  awaiting_resubmission: "bg-secondary/15 text-secondary border-secondary/30",
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "all", category: "all", status: "all", from: "", to: "" });
  const [details, setDetails] = useState(null);
  const [resubmit, setResubmit] = useState(null);
  const [resubmitForm, setResubmitForm] = useState({});
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type !== "all") params.set("type", filters.type);
      if (filters.category !== "all") params.set("category", filters.category);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      params.set("limit", "100");
      const { data } = await api.get(`/transactions?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refreshTxnStatus = async () => {
    if (!details?.id) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/transactions/${details.id}/refresh-status`);
      if (data?.transaction) {
        setDetails(data.transaction);
        const svc = data.service === "bbps" ? "BBPS" : data.service === "payout" ? "Payout" : "";
        if (data.result?.utr) {
          toast.success(`${svc} status refreshed · UTR: ${data.result.utr}`);
        } else if (data.result?.operator_ref) {
          toast.success(`${svc} status refreshed · Operator ref: ${data.result.operator_ref}`);
        } else {
          toast.success(`${svc} status refreshed`);
        }
        load();
      } else {
        const svc = data?.service === "bbps" ? "BBPS" : data?.service === "payout" ? "Payout" : "Provider";
        toast.info(`${svc}: ${data?.message || "Nothing new yet."}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setWorking(false);
    }
  };

  const openResubmit = async (txn) => {
    if (txn.type === "payout" && beneficiaries.length === 0) {
      try {
        const { data } = await api.get("/beneficiaries");
        setBeneficiaries(data || []);
      } catch { /* ignore */ }
    }
    setResubmitForm(
      txn.type === "bbps"
        ? { consumer_id: txn.consumer_id || "", consumer_name: txn.consumer_name || "", amount: txn.amount }
        : { beneficiary_id: txn.beneficiary_id, amount: txn.amount, remarks: txn.remarks || "" }
    );
    setResubmit(txn);
  };

  const submitResubmit = async () => {
    if (!resubmit) return;
    setWorking(true);
    try {
      const path = resubmit.type === "bbps"
        ? `/transactions/${resubmit.id}/resubmit/bbps`
        : `/transactions/${resubmit.id}/resubmit/payout`;
      await api.post(path, resubmitForm);
      toast.success("Resubmitted · status reset to L1 review");
      setResubmit(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Resubmit failed");
    } finally {
      setWorking(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["ID", "Type", "Category", "Amount", "Platform Fee", "Margin", "Net Earnings", "Status", "Ref", "Created"],
      ...items.map((t) => [
        t.id, t.type, t.category, t.amount, t.platform_fee, t.partner_margin, t.net_earnings, t.status, t.provider_ref || "", t.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `flexpay-transactions-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Transactions</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Transaction history
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Every BBPS and payout transaction with full fee breakdown. Filter, inspect, export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv">
            <Download className="h-4 w-4 mr-1" strokeWidth={1.5} /> Export CSV
          </Button>
        </div>
      </div>

      <div className="mt-6 surface-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="txn-filters">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="bbps">BBPS</SelectItem>
              <SelectItem value="payout">Payout</SelectItem>
              <SelectItem value="wallet">Wallet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Category / Rail</Label>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {BBPS_CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="imps">IMPS</SelectItem>
              <SelectItem value="neft">NEFT</SelectItem>
              <SelectItem value="rtgs">RTGS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to" />
        </div>
        <div className="flex items-end">
          <Button className="w-full" onClick={load} data-testid="apply-filters">
            <Filter className="h-4 w-4 mr-1" strokeWidth={1.5} /> Apply
          </Button>
        </div>
      </div>

      <div className="mt-6 surface-card overflow-hidden">
        <div className="px-6 py-3 flex items-center justify-between text-sm">
          <span>
            <span className="font-mono font-semibold">{items.length}</span>
            <span className="text-muted-foreground"> of {total} transactions</span>
          </span>
        </div>
        <Table data-testid="transactions-table">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Txn ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category / Rail</TableHead>
              <TableHead>Consumer / Beneficiary</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead className="text-right">Earnings</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No transactions yet.</TableCell></TableRow>
            ) : (
              items.map((t) => (
                <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                  <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </TableCell>
                  <TableCell><CopyableId value={t.display_id || t.id} /></TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        t.type === "wallet"
                          ? `uppercase font-mono text-[10px] ${t.wallet_kind === "credit" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`
                          : "uppercase font-mono text-[10px]"
                      }
                    >
                      {t.type === "wallet" ? `Wallet ${t.wallet_kind || ""}`.trim() : t.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {t.type === "wallet"
                      ? (t.category || "—").replace(/_/g, " ")
                      : (CATEGORY_LABEL[t.category] || RAIL_LABEL[t.category] || t.category)}
                  </TableCell>
                  <TableCell>
                    {t.type === "bbps" ? (
                      <div>
                        <div>{t.consumer_name || "—"}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{t.consumer_id}</div>
                      </div>
                    ) : t.type === "wallet" ? (
                      <div>
                        <div className="text-xs">{t.reason || (t.wallet_kind === "credit" ? "Wallet credit" : "Wallet debit")}</div>
                        {t.ref_id && (
                          <div className="text-[10px] font-mono text-muted-foreground">
                            ref: {String(t.ref_id).slice(0, 16)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div>{t.beneficiary_snapshot?.nickname}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {t.beneficiary_snapshot?.upi_id ||
                            `••••${(t.beneficiary_snapshot?.account_number || "").slice(-4)}`}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {t.type === "wallet" && t.wallet_kind === "debit" ? "−" : ""}{formatINR(t.amount)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                    {t.type === "wallet" ? "—" : formatINR(t.platform_fee)}
                  </TableCell>
                  <TableCell className={`text-right font-mono tabular-nums ${t.type === "wallet" ? "text-muted-foreground" : (t.net_earnings > 0 ? "text-success" : "text-destructive")}`}>
                    {t.type === "wallet"
                      ? (t.balance_after != null ? `bal ${formatINR(t.balance_after)}` : "—")
                      : `${t.net_earnings >= 0 ? "+" : ""}${formatINR(t.net_earnings)}`}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLE[t.status] || ""}>{t.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setDetails(t)} data-testid={`txn-view-${t.id}`} aria-label="View"><Eye className="h-4 w-4" strokeWidth={1.5} /></Button>
                    {t.status === "awaiting_resubmission" && (
                      <Button size="sm" variant="outline" className="ml-1" onClick={() => openResubmit(t)} data-testid={`txn-resubmit-${t.id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Resubmit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Details dialog */}
      <Dialog open={details !== null} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
              <span>Transaction details</span>
              {details && (details.type === "payout" || details.type === "bbps") && (
                <Button
                  size="sm" variant="outline" disabled={working}
                  onClick={refreshTxnStatus}
                  data-testid="refresh-status-btn"
                  className="text-xs"
                  title={details.type === "bbps"
                    ? "Re-query BBPS provider for bill payment status & operator ref"
                    : "Re-query payout provider for UTR / settlement state"}
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${working ? "animate-spin" : ""}`} />
                  Refresh {details.type === "bbps" ? "BBPS" : "payout"} status
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>Full audit trail including approval timeline.</DialogDescription>
          </DialogHeader>
          {details && (
            <div className="space-y-4 text-sm">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><div className="overline">Status</div><div className="mt-1 font-mono">{details.status}</div></div>
                <div><div className="overline">Provider ref</div><div className="mt-1 font-mono">{details.provider_ref || "—"}</div></div>
                {(details.bank_utr || details.payout_last_status_response?.utr) && (
                  <div data-testid="txn-utr">
                    <div className="overline">Bank UTR</div>
                    <div className="mt-1 font-mono">
                      {details.bank_utr || details.payout_last_status_response?.utr}
                    </div>
                  </div>
                )}
                {details.payout_last_status_response?.operator_ref &&
                  !details.bank_utr &&
                  !details.payout_last_status_response?.utr && (
                  <div data-testid="txn-operator-ref"><div className="overline">Operator ref</div><div className="mt-1 font-mono">{details.payout_last_status_response.operator_ref}</div></div>
                )}
                <div><div className="overline">Amount</div><div className="mt-1 font-mono">{formatINR(details.amount)}</div></div>
                <div><div className="overline">Wallet debit</div><div className="mt-1 font-mono">{formatINR(details.wallet_debit)}</div></div>
              </div>
              {details.approval_chain?.length > 0 && (
                <div>
                  <span className="overline">Approval timeline</span>
                  <ol className="mt-2 space-y-1 text-xs">
                    {details.approval_chain.map((e, i) => (
                      <li key={i} className="border-l-2 border-border pl-3 py-1">
                        <span className="font-semibold">L{e.level}</span> · {e.action}
                        {e.remarks && <span className="text-muted-foreground"> — {e.remarks}</span>}
                        <div className="text-muted-foreground font-mono text-[10px]">{new Date(e.decided_at).toLocaleString()} · {e.approver_email}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {(details.payout_last_status_response || details.payout_status_checked_at) && (
                <div data-testid="status-check-section">
                  <span className="overline">Provider status check</span>
                  <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-xs space-y-1.5">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <div><span className="text-muted-foreground">Last checked:</span> <span className="font-mono">{details.payout_status_checked_at ? new Date(details.payout_status_checked_at).toLocaleString() : "—"}</span></div>
                      <div><span className="text-muted-foreground">Provider says:</span> <span className="font-mono font-semibold">{details.payout_last_status_response?.status || "—"}</span></div>
                      {details.payout_last_status_response?.operator_ref && (
                        <div><span className="text-muted-foreground">Operator ref / UTR:</span> <span className="font-mono">{details.payout_last_status_response.operator_ref}</span></div>
                      )}
                      {details.payout_last_status_response?.date_added && (
                        <div><span className="text-muted-foreground">Provider date:</span> <span className="font-mono">{details.payout_last_status_response.date_added}</span></div>
                      )}
                    </div>
                    {details.payout_last_status_response?.message && (
                      <div className="text-muted-foreground italic">{details.payout_last_status_response.message}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDetails(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resubmit dialog */}
      <Dialog open={resubmit !== null} onOpenChange={(o) => !o && setResubmit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resubmit transaction</DialogTitle>
            <DialogDescription>
              Re-enter the {resubmit?.type === "bbps" ? "card / consumer" : "beneficiary"} details. Wallet will be re-debited and the request will go back to L1 review.
            </DialogDescription>
          </DialogHeader>
          {resubmit?.approval_chain?.length > 0 && (
            <div className="rounded-md border border-secondary/40 bg-secondary/5 p-3 text-xs">
              <div className="overline text-secondary">Reviewer note</div>
              <div className="mt-1">
                {resubmit.approval_chain.filter((e) => e.action === "request_resubmit").slice(-1)[0]?.remarks || "—"}
              </div>
            </div>
          )}
          {resubmit && resubmit.type === "bbps" && (
            <div className="space-y-3">
              <div>
                <Label>Card / consumer number</Label>
                <Input
                  data-testid="resubmit-consumer-id"
                  className="mt-1 font-mono"
                  value={resubmitForm.consumer_id || ""}
                  onChange={(e) => setResubmitForm({ ...resubmitForm, consumer_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Consumer name (optional)</Label>
                <Input
                  data-testid="resubmit-consumer-name"
                  className="mt-1"
                  value={resubmitForm.consumer_name || ""}
                  onChange={(e) => setResubmitForm({ ...resubmitForm, consumer_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number" min="1" step="any"
                  data-testid="resubmit-amount"
                  className="mt-1 font-mono"
                  value={resubmitForm.amount || ""}
                  onChange={(e) => setResubmitForm({ ...resubmitForm, amount: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          )}
          {resubmit && resubmit.type === "payout" && (
            <div className="space-y-3">
              <div>
                <Label>Beneficiary</Label>
                <Select value={resubmitForm.beneficiary_id} onValueChange={(v) => setResubmitForm({ ...resubmitForm, beneficiary_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="resubmit-bene"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {beneficiaries.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.nickname} · {b.type === "upi" ? b.upi_id : `••••${(b.account_number || "").slice(-4)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number" min="1" step="any"
                  data-testid="resubmit-amount"
                  className="mt-1 font-mono"
                  value={resubmitForm.amount || ""}
                  onChange={(e) => setResubmitForm({ ...resubmitForm, amount: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Input
                  data-testid="resubmit-remarks"
                  className="mt-1"
                  value={resubmitForm.remarks || ""}
                  onChange={(e) => setResubmitForm({ ...resubmitForm, remarks: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResubmit(null)}>Cancel</Button>
            <Button onClick={submitResubmit} disabled={working} data-testid="confirm-resubmit">
              {working ? "Submitting…" : "Resubmit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
